import { useEffect, useRef, useState, type ReactNode } from 'react'
// v6 dropped maplibre-gl's default export; `Map` is a named export that doubles
// as both the constructor and the instance type.
import { Map as MapLibreMap, setMaxParallelImageRequests, setWorkerUrl } from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
// maplibre-gl resolves its worker at runtime from `new URL('./' + name,
// import.meta.url)`. That template string is invisible to the bundler, so Vite
// never emits the worker file and the library asks for a URL that 404s in
// production. `?worker&url` makes Vite bundle the worker (together with the
// `./maplibre-gl-shared.mjs` it imports) and hand back a hashed URL, which the
// library's own setter then points it at.
import maplibreWorkerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url'
import { MapContext } from './mapContext'
import type { RasterSourceSpec } from './tangerSource'
import { GEOJSON_LAYER_PREFIX, MAP_MAX_ZOOM, MAP_MIN_ZOOM } from './constants'

setWorkerUrl(maplibreWorkerUrl)

/** Halved from maplibre-gl's default of 16. Global, because the library offers
 *  no per-source equivalent: shorter request bursts against the Tanger
 *  tileserver, which admits a bounded number of concurrent requests and 503s
 *  the overflow. */
setMaxParallelImageRequests(8)

// Re-exported here (rather than only living in ./constants) to keep the
// public barrel's import path unchanged — see ./constants for why the values
// themselves moved out.
export { GEOJSON_LAYER_PREFIX, MAP_MAX_ZOOM, MAP_MIN_ZOOM }

const BASE_LAYER_ID = 'raster-base'
const OVERLAY_LAYER_PREFIX = 'raster-overlay-'

export interface MapViewport {
  center: { lat: number; lon: number }
  zoom: number
}

export interface MapViewProps {
  /** Base raster layer, or null before the catalogue has answered. Object
   *  identity does not need to be stable across renders — like
   *  `overlaySources` below, the reconciliation effect keys off a content
   *  signature (tiles/tileSize/min/maxzoom), not the reference, so a caller
   *  that resolves a fresh object each render does not cause the base layer
   *  to be torn down and re-added. */
  baseSource?: RasterSourceSpec | null
  /** Transparent overlay rasters, composited above the base in list order.
   *  Array identity does not need to be stable across renders — the
   *  reconciliation effect keys off a content signature (tiles/tileSize/
   *  min/maxzoom), not the array reference, so a freshly-mapped array each
   *  render does not cause layers to be torn down and re-added. */
  overlaySources?: RasterSourceSpec[]
  /** [west, south, east, north]; fitted once on load when present. */
  initialBounds?: [number, number, number, number] | null
  /** Used on load when there are no bounds (the "regional overview"). */
  initialView?: MapViewport
  interactive?: boolean
  /**
   * MapLibre's own keyboard handler: arrow keys pan 100px, `+`/`-` zoom,
   * `Shift`+arrow rotates/pitches (rotate/pitch are separately stripped below
   * via `disableRotation()` regardless of this flag). Default `true`, matching
   * MapLibre's own default. A surface that owns its OWN arrow/space keyboard
   * shortcuts — the replay viewer's skip/scrub/play-pause — must pass `false`:
   * the map canvas is focusable (`tabindex=0`), and `disableRotation()` alone
   * leaves plain arrow-key panning live, so a focused canvas would otherwise
   * both pan the camera AND trigger the surface's own shortcut on the same
   * keypress.
   */
  keyboard?: boolean
  minZoom?: number // default 4
  maxZoom?: number // default 18
  /** Fired on moveend and once after load. Pass a stable callback. */
  onViewChange?: (view: MapViewport) => void
  /** Fired when a user gesture moves the map (drives "recenter" enablement). */
  onUserMove?: () => void
  /** Rendered instead of the map when map creation fails (no WebGL). */
  errorFallback?: ReactNode
  /** Extra class for the container div (sizing comes from the host). */
  className?: string
  children?: ReactNode
}

const EMPTY_OVERLAYS: RasterSourceSpec[] = []

/** The first geojson layer currently on the map, if any — the raster anchor. */
function vectorAnchorLayerId(map: MapLibreMap): string | undefined {
  return map.getStyle().layers?.find((l) => l.id.startsWith(GEOJSON_LAYER_PREFIX))?.id
}

export function MapView({
  baseSource,
  overlaySources,
  initialBounds,
  initialView,
  interactive = true,
  keyboard = true,
  minZoom = MAP_MIN_ZOOM,
  maxZoom = MAP_MAX_ZOOM,
  onViewChange,
  onUserMove,
  errorFallback,
  className,
  children,
}: MapViewProps) {
  const container = useRef<HTMLDivElement>(null)
  // The live map once (and only once) its style has loaded — gating children on
  // this is what lets every layer/marker child call addSource/addLayer
  // unconditionally instead of each re-implementing load-timing guards.
  const [map, setMap] = useState<MapLibreMap | null>(null)
  const [failed, setFailed] = useState(false)

  // Mount-time props, read through refs so the mount effect can have [] deps:
  // re-creating the map on a prop change would throw away the user's pan/zoom.
  const initialRef = useRef({ initialBounds, initialView, interactive, keyboard, minZoom, maxZoom })
  initialRef.current = { initialBounds, initialView, interactive, keyboard, minZoom, maxZoom }
  const onViewChangeRef = useRef(onViewChange)
  onViewChangeRef.current = onViewChange
  const onUserMoveRef = useRef(onUserMove)
  onUserMoveRef.current = onUserMove

  useEffect(() => {
    if (!container.current) return
    // A style load is network-bound, so it can resolve after unmount (or after
    // React 18 StrictMode's deliberate dev-time mount→unmount→remount).
    let cancelled = false
    const init = initialRef.current
    let instance: MapLibreMap
    try {
      instance = new MapLibreMap({
        container: container.current,
        // Empty style; every source and layer is added programmatically — one
        // uniform "add a layer once the map is ready" path.
        style: { version: 8, sources: {}, layers: [] },
        attributionControl: { compact: true },
        interactive: init.interactive,
        dragRotate: false,
        pitchWithRotate: false,
        touchPitch: false,
        keyboard: init.keyboard,
        minZoom: init.minZoom,
        maxZoom: init.maxZoom,
      })
    } catch {
      // WebGL unavailable. The host renders errorFallback; nothing to clean up.
      setFailed(true)
      return
    }
    instance.touchZoomRotate.disableRotation()
    // Harmless (and left unconditional rather than guarded on `init.keyboard`)
    // even when the keyboard option above disabled the handler entirely:
    // `disableRotation()` only flips an internal flag the handler's own
    // `keydown` reads, with no side effect of its own — verified against
    // maplibre-gl's `KeyboardHandler` source.
    instance.keyboard?.disableRotation?.()

    const report = () => {
      const c = instance.getCenter()
      onViewChangeRef.current?.({ center: { lat: c.lat, lon: c.lng }, zoom: instance.getZoom() })
    }
    instance.on('moveend', report)
    // A movestart carrying originalEvent is a user gesture, not a fitBounds/jumpTo.
    let userMoved = false
    instance.on('movestart', (e) => {
      if ((e as { originalEvent?: unknown }).originalEvent) {
        userMoved = true
        onUserMoveRef.current?.()
      }
    })

    let loaded = false
    const fitInitialBounds = () => {
      if (init.initialBounds) instance.fitBounds(init.initialBounds, { animate: false, padding: 24 })
    }

    // MapLibre sizes its canvas once, at construction, and then deliberately
    // ignores the FIRST delivery of its own ResizeObserver on the assumption
    // that it merely echoes that size. A host that grows the container right
    // after mount — the team Map tab measures its fill height in an effect and
    // applies it a moment later — can land that growth before the first
    // delivery, and MapLibre then never learns of it: the WebGL canvas keeps the
    // construction-time size while the DOM markers follow the container, until
    // the next window resize. Own the observation here and resize on every
    // delivery, the first included; `resize()` at an unchanged size is cheap.
    //
    // Until the first user gesture the view is still the initial framing, so a
    // fit computed against a container that has since grown is refitted to the
    // size the container actually settled at.
    const observer =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(() => {
            if (cancelled) return
            instance.resize()
            if (loaded && !userMoved) fitInitialBounds()
          })
    observer?.observe(container.current)

    instance.once('load', () => {
      if (cancelled) return
      loaded = true
      if (init.initialBounds) {
        fitInitialBounds()
      } else if (init.initialView) {
        instance.jumpTo({
          center: [init.initialView.center.lon, init.initialView.center.lat],
          zoom: init.initialView.zoom,
        })
      }
      setMap(instance)
      report()
    })

    return () => {
      cancelled = true
      observer?.disconnect()
      setMap(null)
      instance.remove()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Base + overlay rasters: declarative reconciliation. Remove-and-re-add on
  // any actual CONTENT change — simple over clever; if base-map switching
  // visibly flashes, the ASF heatmap's generation-swap-on-idle pattern is the
  // known upgrade.
  const overlays = overlaySources ?? EMPTY_OVERLAYS
  // Both keys are content signatures (tiles/tileSize/min/maxzoom), not object
  // identity. This used to be true for overlays only, with a comment calling
  // a reference dep on `baseSource` "acceptable" because it is "a single
  // object" — disproven by the editor canvas: it now re-renders once per
  // camera tick (`onViewChange` drives page state there), so `baseSource`
  // being freshly resolved every render turned every zoom step into a
  // remove+re-add of the base raster layer — a black flash and a full retile
  // from a source whose actual tiles/zoom range never changed. A caller
  // mapping/resolving a fresh object or array each render (both are
  // documented as fine on the props above) must never fail this effect's
  // identity check for content that didn't change.
  const sourceKey = (s: RasterSourceSpec) => `${s.tiles.join(',')}|${s.tileSize}|${s.minzoom ?? ''}|${s.maxzoom ?? ''}`
  const baseKey = baseSource ? sourceKey(baseSource) : null
  const overlaysKey = overlays.map(sourceKey).join(';')
  // Read through refs inside the effect: the *Key strings, not baseSource/
  // overlays themselves, drive re-running the effect, so the effect body
  // needs a way to see the current values even on a run triggered by some
  // other dependency (map) changing while both keys stay the same.
  const baseRef = useRef(baseSource)
  baseRef.current = baseSource
  const overlaysRef = useRef(overlays)
  overlaysRef.current = overlays
  useEffect(() => {
    if (!map) return
    const desired: { id: string; spec: RasterSourceSpec }[] = [
      ...(baseRef.current ? [{ id: BASE_LAYER_ID, spec: baseRef.current }] : []),
      ...overlaysRef.current.map((spec, i) => ({ id: `${OVERLAY_LAYER_PREFIX}${i}`, spec })),
    ]
    const anchor = vectorAnchorLayerId(map)
    for (const { id, spec } of desired) {
      if (map.getLayer(id)) map.removeLayer(id)
      if (map.getSource(id)) map.removeSource(id)
      map.addSource(id, spec)
      map.addLayer({ id, type: 'raster', source: id }, anchor)
    }
    return () => {
      for (const { id } of desired) {
        if (map.getLayer(id)) map.removeLayer(id)
        if (map.getSource(id)) map.removeSource(id)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, baseKey, overlaysKey])

  if (failed) return <>{errorFallback ?? null}</>
  return (
    <div ref={container} className={className} style={{ position: 'absolute', inset: 0 }}>
      {map && <MapContext.Provider value={map}>{children}</MapContext.Provider>}
    </div>
  )
}
